import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { ErrorBox, Field, Loading, ThemeSelect } from '../../shared/ui';
import { PoweredBy, ProvaliaLogoStacked } from '../../shared/brand';
import { api, type PortalInfo } from '../api';
import { TenantLogo } from './Login';

/**
 * Cuenta sin sesión: crear la contraseña con el enlace de la invitación,
 * elegir una nueva con el de «¿Has olvidado tu contraseña?», y pedir ese
 * enlace.
 */

export function AuthShell({ info, children }: { info: PortalInfo; children: ReactNode }): JSX.Element {
  return (
    <div className="login-page">
      <div className="stack lg" style={{ width: '100%', maxWidth: 420 }}>
        <div className="card login-card">
          <div className="card-body">
            <div className="login-brand">
              <ProvaliaLogoStacked height={80} />
              <div className="pt-login-client">
                <TenantLogo info={info} className="pt-login-client-logo" nameFallback={false} />
                <h1 className="pt-login-title">{info.name}</h1>
                <div className="muted">Portal de proveedores</div>
              </div>
            </div>
            {children}
          </div>
        </div>
        <div className="row between">
          <PoweredBy />
          <ThemeSelect />
        </div>
      </div>
    </div>
  );
}

/** Pide el enlace para elegir una contraseña nueva. Responde igual exista o no la cuenta. */
export function ForgotPassword({ info, onBack }: { info: PortalInfo; onBack: () => void }): JSX.Element {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<string | null>(null);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ mensaje: string }>('/portal/account/forgot', { email: email.trim() });
      setDone(res.mensaje);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell info={info}>
      {done ? (
        <div className="stack">
          <div className="callout ok">
            <div className="title">Revisa tu correo</div>
            {done} El enlace caduca en 1 hora. Si no lo ves, mira en la carpeta de correo no deseado.
          </div>
          <button className="btn block pt-big" onClick={onBack}>
            Volver a la entrada
          </button>
        </div>
      ) : (
        <form className="stack" onSubmit={(e) => void submit(e)}>
          <h2>¿Has olvidado tu contraseña?</h2>
          <p className="muted">Escribe el correo con el que entras y te mandaremos un enlace para elegir una nueva.</p>
          <Field label="Correo">
            <input
              className="input pt-big"
              type="email"
              autoComplete="username"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
          </Field>
          <ErrorBox error={error} />
          <button className="btn primary block pt-big" type="submit" disabled={busy || !email.includes('@')}>
            {busy ? 'Enviando…' : 'Enviarme el enlace'}
          </button>
          <button className="btn ghost block" type="button" onClick={onBack}>
            Volver a la entrada
          </button>
        </form>
      )}
    </AuthShell>
  );
}

interface TokenInfo {
  email: string;
  name: string;
  purpose: 'invite' | 'reset';
  tenant: string;
}

/** Comprobaciones mínimas a la vista, para no esperar al error del servidor. */
function passwordProblems(p: string, email: string): string[] {
  const out: string[] = [];
  if (p.length < 8) out.push('Al menos 8 caracteres');
  if (!/[A-Za-zÀ-ÿ]/.test(p) || !/\d/.test(p)) out.push('Letras y al menos un número');
  if (email && p.toLowerCase().includes(email.split('@')[0]?.toLowerCase() ?? '\u0000')) out.push('Que no contenga tu usuario');
  return out;
}

/** Crear la contraseña (invitación) o elegir una nueva (recuperación) con el enlace del correo. */
export function SetPassword({ info, token, onDone }: { info: PortalInfo; token: string; onDone: (email: string) => void }): JSX.Element {
  const [data, setData] = useState<TokenInfo | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    api
      .get<TokenInfo>(`/portal/account/token/${encodeURIComponent(token)}`)
      .then(setData)
      .catch(setLoadError);
  }, [token]);

  const problems = passwordProblems(password, data?.email ?? '');
  const mismatch = repeat !== '' && repeat !== password;

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (problems.length > 0 || mismatch) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ email: string }>(`/portal/account/token/${encodeURIComponent(token)}`, { password });
      onDone(res.email);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <AuthShell info={info}>
        <div className="stack">
          <ErrorBox error={loadError} />
          <a className="btn block pt-big" href="/">
            Ir a la entrada
          </a>
        </div>
      </AuthShell>
    );
  }
  if (!data) return <Loading label="Comprobando el enlace…" />;

  const invite = data.purpose === 'invite';
  return (
    <AuthShell info={info}>
      <form className="stack" onSubmit={(e) => void submit(e)}>
        <h2>{invite ? `Hola, ${data.name}` : 'Elige una contraseña nueva'}</h2>
        <p className="muted">
          {invite
            ? `Crea tu contraseña para entrar en el portal de proveedores de ${data.tenant}. Tu usuario es ${data.email}.`
            : `Para la cuenta ${data.email}. Al guardarla se cerrarán las sesiones que tengas abiertas.`}
        </p>
        {/* Para que el gestor de contraseñas del navegador la guarde con el usuario correcto. */}
        <input type="email" autoComplete="username" value={data.email} readOnly hidden />
        <Field label="Contraseña">
          <input
            className="input pt-big"
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoFocus
          />
        </Field>
        <Field label="Repite la contraseña" error={mismatch ? 'No coincide con la anterior' : null}>
          <input
            className="input pt-big"
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
            required
          />
        </Field>
        <label className="check">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} /> Ver la contraseña
        </label>
        {password !== '' && problems.length > 0 ? (
          <ul className="subtle" style={{ margin: 0, paddingLeft: 18 }}>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        ) : null}
        <ErrorBox error={error} />
        <button className="btn primary block pt-big" type="submit" disabled={busy || problems.length > 0 || mismatch || repeat === ''}>
          {busy ? 'Guardando…' : invite ? 'Crear mi contraseña y entrar' : 'Guardar la contraseña'}
        </button>
      </form>
    </AuthShell>
  );
}
